import React, { useState, useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  CharacterItem,
  Character,
  MagicItemKeyword,
  Spell,
  SpellLimitation,
  CharacterMagicItemKeyword,
} from "@dndModels";
import { ALL_DRAGONS, CREATURE_TYPES } from "@dndModels/Constants";

import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import DataUtilities from '@dndUtilities/data_utilities';
import { makeStyles } from "@utilities/theme_utilities";

import ViewItem from "@dndComponents/display/ViewItem";
import ButtonBox from "@components/input/buttons/ButtonBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectSpellBox from "@dndComponents/select/SelectSpellBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props {
  character: Character;
  log_it?: boolean;
  char_item: CharacterItem;
  cmik: CharacterMagicItemKeyword;
  onChange: () => void;
}

const connect_util = CharConnectUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterItemMIK({
  character,
  log_it = false,
  char_item: item,
  cmik,
  onChange,
}: Props) {

  // const renderCount = useRef(0);
  // useEffect(() => {
  //   renderCount.current += 1;
  //   console.log(`CharacterItemMIK render #${renderCount.current}`);
  // });
  const mik = cmik.source ?? new MagicItemKeyword();

  const [extra_string, setExtraString] = useState(item.magic_item_keyword_ids.includes(mik._id) && mik.extra_base_type !== "None" ? item.extra : "");
  const [extra_spell, setExtraSpell] = useState<Spell | null>(item.magic_item_keyword_ids.includes(mik._id) && mik.extra_base_type !== "None" ? item.extra_spell : null);
  const [show_details, setShowDetails] = useState(false);
  const [show_spell, setShowSpell] = useState(false);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        border: "1px solid"
      },
    });
  });

  const { classes, cx } = useStyles();

  const spell_limit = useMemo(() =>
    mik.extra_base_limit.limits.length > 0 && mik.extra_base_limit.limits[0] instanceof SpellLimitation
      ? mik.extra_base_limit.limits[0]
      : new SpellLimitation(),
    [mik]
  );

  const handleSpellChange = useCallback((changed: string) => {
    const spell = data_util.spells.find(o => o._id === changed);
    item.extra = changed;
    if (spell) {
      item.extra_spell = spell;
      setExtraSpell(spell);
    }
    setExtraString(changed);
    onChange();
  }, []);

  // console.log("Rendering Char Item MIK");
  if (mik.extra_base_type === "None") {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={8}
          className="clickable"
          onClick={() => {
            setShowDetails(!show_details);
          }}>
          <ViewItem item={mik} />
        </Grid>
        <Grid size={4}>
          <ButtonBox
            name="Remove"
            onClick={async () => {
              item.sub_char_feature_sources = 
                item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
              if (mik.min_rarity !== mik.max_rarity) {
                item.selected_rarity = -1;
              }
              onChange();
            }}
          />
        </Grid>
        { show_details &&
          <Grid
            size={12}
            className={cx(classes.root)}>
            <DisplayStringWithReplacements cfs={character} display_string={mik.description} />
          </Grid>
        }
      </Grid>
    );
  } else if (mik.extra_base_type === "Spell") {
    return (
      <>
        <Grid container spacing={0} direction="row">
          <Grid
            size={4}
            className="clickable"
            onClick={() => {
              setShowDetails(!show_details);
            }}><ViewItem item={mik} /></Grid>
          <Grid size={4}>
            <SelectSpellBox 
              name="Spell"
              value={extra_spell?._id ?? ""}
              spell_limit={spell_limit}
              onChange={handleSpellChange}
            />
          </Grid>
          <Grid size={4}>
            <ButtonBox
              name="Remove"
              onClick={async () => {
                item.sub_char_feature_sources = 
                  item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
                item.extra = "";
                item.extra_spell = null;
                if (mik.min_rarity !== mik.max_rarity) {
                  item.selected_rarity = -1;
                }
                onChange();
              }}
            />
          </Grid>
          { show_details &&
            <Grid size={12}
              className={cx(classes.root)}>
              <DisplayStringWithReplacements cfs={character} display_string={mik.description} />
            </Grid>
          }
          { extra_spell && 
            <>
              <Grid size={12}>
                <ToggleButtonBox
                  name="Show Spell Description"
                  value={show_spell}
                  onToggle={() => { setShowSpell(!show_spell); }}
                />
              </Grid>
              { show_spell && 
                <Grid size={12}
                  className={cx(classes.root)}>
                  <DisplayStringWithReplacements cfs={character} display_string={extra_spell.description} />
                </Grid>
              }
            </> 
          }
        </Grid>
      </>
    );
  } else {
    let extra_options: string[] = [];
    if (mik.extra_base_type === "Dragon Breed") {
      const extra_base_limits = mik.extra_base_limits as string[];
      extra_options = extra_base_limits.length > 0 ? extra_base_limits : ALL_DRAGONS;
    } else if (mik.extra_base_type === "Creature Type") {
      const extra_base_limits = mik.extra_base_limits as string[];
      extra_options = extra_base_limits.length > 0 ? extra_base_limits : CREATURE_TYPES;
    }
    return (
      <>
        <Grid container spacing={0} direction="row">
          <Grid
            size={4}
            className="clickable"
            onClick={() => {
              setShowDetails(!show_details);
            }}><ViewItem item={mik} /></Grid>
          <Grid size={4}>
            <SelectStringBox
              name={mik.extra_base_type}
              options={extra_options}
              value={extra_string}
              onChange={(changed: string) => {
                const new_char_item = item;
                new_char_item.extra = changed;
                setExtraString(changed);
                onChange();
              }}
            />
          </Grid>
          <Grid size={4}>
            <ButtonBox
              name="Remove"
              onClick={async () => {
                const new_char_item = item;
                item.sub_char_feature_sources = 
                  item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
                new_char_item.extra = "";
                new_char_item.extra_spell = null;
                if (mik.min_rarity !== mik.max_rarity) {
                  item.selected_rarity = -1;
                }
                onChange();
              }}
            />
          </Grid>
          { show_details &&
            <Grid
              size={4}
              className={cx(classes.root)}>
              <DisplayStringWithReplacements cfs={character} display_string={mik.description} />
            </Grid>
          }
        </Grid>
      </>
    );
  }
}